import { useEffect, useState } from "react";
import { api, formatTRY, formatApiErrorDetail, toNum, todayISO } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartHandshake, Plus, Pencil, Trash2, Users, Utensils, Wallet, AlertCircle } from "lucide-react";
import { MemberCard } from "@/components/club/MemberCard";
import { ClubPL } from "@/components/club/ClubPL";
import { ClubMonthly } from "@/components/club/ClubMonthly";
import { ClubAttendance } from "@/components/club/ClubAttendance";
import { useAuth } from "@/context/AuthContext";
import { FinanceDetailDialog } from "@/components/FinanceDetailDialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const EMPTY = { name: "", phone: "", email: "", note: "", active: true };

export default function Club() {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [memberDlg, setMemberDlg] = useState(null);
  const [pkgDlg, setPkgDlg] = useState(null);
  const [payDlg, setPayDlg] = useState(null);
  const [usageDlg, setUsageDlg] = useState(null);
  const [mealDlg, setMealDlg] = useState(null);
  const [mealProduct, setMealProduct] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const [m, s, mp] = await Promise.all([api.get("/club/members"), api.get("/club/summary"), api.get("/club/meal-product")]);
    setMembers(m.data); setSummary(s.data); setMealProduct(mp.data);
  };
  useEffect(() => { load(); if (user?.role === "admin") api.get("/recipes").then((r) => setRecipes(r.data.filter((x) => !x.template_id))); }, [user?.role]);

  const saveMealProduct = async (v) => {
    try {
      await api.put("/club/meal-product", { recipe_id: v === "none" ? null : v });
      toast.success("Öğün ürünü kaydedildi"); load();
    } catch (e) { err(e); }
  };

  const run = async (fn, msg) => { try { const r = await fn(); toast.success(typeof msg === "function" ? msg(r) : msg); load(); return true; } catch (e) { err(e); return false; } };

  const saveMember = async () => {
    const { id, ...body } = memberDlg;
    if (!body.name.trim()) return toast.error("Ad zorunlu");
    const ok = await run(() => (id ? api.put(`/club/members/${id}`, body) : api.post("/club/members", body)), id ? "Üye güncellendi" : "Üye eklendi");
    if (ok) setMemberDlg(null);
  };
  const savePkg = async () => {
    const ok = await run(() => api.post(`/club/members/${pkgDlg.memberId}/packages`, { meals: toNum(pkgDlg.meals), price: toNum(pkgDlg.price), note: pkgDlg.note, date: pkgDlg.date || null }), "Paket tanımlandı");
    if (ok) setPkgDlg(null);
  };
  const savePay = async () => {
    if (!(toNum(payDlg.amount) > 0)) return toast.error("Tutar girin");
    if (toNum(payDlg.commission_amount) > toNum(payDlg.amount)) return toast.error("Sahip payı tahsilat tutarını aşamaz");
    const body = { amount: toNum(payDlg.amount), payment_type: payDlg.payment_type, date: payDlg.date, note: payDlg.note, commission_amount: toNum(payDlg.commission_amount), owner_name: payDlg.owner_name };
    const ok = await run(() => payDlg.payId
      ? api.put(`/club/members/${payDlg.memberId}/packages/${payDlg.pkgId}/payments/${payDlg.payId}`, body)
      : api.post(`/club/members/${payDlg.memberId}/packages/${payDlg.pkgId}/payments`, body), payDlg.payId ? "Tahsilat güncellendi" : "Tahsilat kaydedildi");
    if (ok) setPayDlg(null);
  };
  const saveUsage = async () => {
    const n = Math.round(toNum(usageDlg.count));
    if (!(n > 0)) return toast.error("Öğün sayısı girin");
    const ok = await run(() => api.put(`/club/members/${usageDlg.memberId}/packages/${usageDlg.pkgId}/usage/${usageDlg.idx}`, { delta: usageDlg.kind === "use" ? -n : n, date: usageDlg.date || null, note: usageDlg.note }), "Öğün hareketi güncellendi");
    if (ok) setUsageDlg(null);
  };
  const saveMealDated = async () => {
    const n = Math.round(toNum(mealDlg.count));
    if (!(n > 0)) return toast.error("Öğün sayısı girin");
    if (!mealDlg.date) return toast.error("Tarih seçin");
    const ok = await run(() => api.post(`/club/members/${mealDlg.memberId}/packages/${mealDlg.pkgId}/meals`, { delta: -n, date: mealDlg.date, note: mealDlg.note }),
      (r) => `${n} öğün ${mealDlg.date.split("-").reverse().join(".")} tarihine işlendi` + (r.data.stock_product ? ` · stoktan düşüldü: ${r.data.stock_product}` : ""));
    if (ok) setMealDlg(null);
  };

  const filtered = members.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()) || m.phone.includes(q));

  return (
    <div className="space-y-6" data-testid="club-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Beslenme Kulübü</h1>
          <p className="text-sm text-muted-foreground">Üyeler, öğün hakları ve tahsilat takibi</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Üye ara…" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" data-testid="club-search" />
          <Button onClick={() => setMemberDlg({ ...EMPTY })} data-testid="club-add-member"><Plus className="mr-1.5 h-4 w-4" /> Yeni Üye</Button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="club-summary">
          <Stat icon={Users} label="Aktif Üye" value={`${summary.active} / ${summary.members}`} />
          <Stat icon={Utensils} label="Toplam Kalan Öğün" value={summary.meals_left} />
          <Stat icon={Wallet} label="Toplam Tahsilat" value={formatTRY(summary.total_paid)} />
          <Stat icon={AlertCircle} label="Bekleyen Bakiye" value={formatTRY(summary.balance)} sub={`${summary.debtors} borçlu üye`} warn={summary.balance > 0} />
        </div>
      )}

      {summary && <ClubPL refreshKey={summary} isAdmin={user?.role === "admin"} />}
      {summary && <ClubMonthly refreshKey={summary} />}
      {summary && <ClubAttendance refreshKey={summary} />}

      {summary && (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="club-finance">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Kulüp Kasası — Fit House kasasından ayrı</p>
              <p className="mt-1 text-sm">
                Tahsilat <button type="button" className="font-bold underline decoration-dotted underline-offset-4 hover:text-primary" data-testid="club-fin-income" onClick={() => setDetail({ kind: "club_income", title: "Kulüp Tahsilat Kayıtları" })}>{formatTRY(summary.club_income)}</button>
                {" · "}Kulüp sahibi payı <button type="button" className="font-bold text-destructive underline decoration-dotted underline-offset-4 hover:opacity-80" data-testid="club-fin-commission" onClick={() => setDetail({ kind: "club_expense", title: "Kulüp Sahibi Payı Kayıtları" })}>−{formatTRY(summary.club_commission)}</button>
                {" · "}Kulüp net <b className="text-primary" data-testid="club-fin-net">{formatTRY(summary.club_net)}</b>
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Tutarlara tıklayarak kayıtları görebilirsiniz · Sahibi payı her tahsilatta ₺ tutar olarak girilir</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid="club-meal-product">
        <Utensils className="h-4 w-4 text-primary" />
        <span className="text-muted-foreground">Her öğün kullanımında stoktan düşülecek ürün:</span>
        {user?.role === "admin" ? (
          <Select value={mealProduct?.recipe_id || "none"} onValueChange={saveMealProduct}>
            <SelectTrigger className="w-72" data-testid="meal-product-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Otomatik ("Beslenme Kulübü…" adlı ürün)</SelectItem>
              {recipes.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`meal-product-${r.id}`}>{r.display_name || r.product_name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : null}
        <span className={`font-semibold ${mealProduct?.resolved ? "text-primary" : "text-destructive"}`} data-testid="meal-product-resolved">
          {mealProduct?.resolved ? `${mealProduct.resolved}${mealProduct.resolved_size ? ` (${mealProduct.resolved_size})` : ""}` : "Tanımlı değil — öğün kullanımı stok düşmez"}
        </span>
      </div>

      <div className="rounded-xl border border-border bg-card" data-testid="club-members">
        {filtered.length === 0 && <p className="flex items-center gap-2 p-8 text-sm text-muted-foreground"><HeartHandshake className="h-4 w-4" /> Henüz üye yok</p>}
        {filtered.map((m) => (
          <MemberCard key={m.id} m={m}
            onEdit={() => setMemberDlg({ id: m.id, name: m.name, phone: m.phone, email: m.email, note: m.note, active: m.active })}
            onDelete={() => window.confirm(`${m.name} silinsin mi?`) && run(() => api.delete(`/club/members/${m.id}`), "Üye silindi")}
            onAddPkg={() => setPkgDlg({ memberId: m.id, meals: 26, price: 4500, note: "", date: todayISO() })}
            onDeletePkg={(p) => window.confirm("Paket ve ödemeleri silinsin mi?") && run(() => api.delete(`/club/members/${m.id}/packages/${p.id}`), "Paket silindi")}
            onMeal={(p, delta) => run(() => api.post(`/club/members/${m.id}/packages/${p.id}/meals`, { delta }),
              (r) => (delta < 0 ? "Öğün kullanıldı" : "Öğün iade edildi") + (r.data.stock_product ? ` · stoktan ${delta < 0 ? "düşüldü" : "iade edildi"}: ${r.data.stock_product}` : " · stok ürünü tanımlı değil"))}
            onPay={(p) => setPayDlg({ memberId: m.id, pkgId: p.id, amount: p.balance > 0 ? p.balance : "", payment_type: "Nakit", date: todayISO(), note: "", commission_amount: "", owner_name: "" })}
            onEditPay={(p, x) => setPayDlg({ memberId: m.id, pkgId: p.id, payId: x.id, amount: x.amount, payment_type: x.payment_type, date: x.date, note: x.note || "", commission_amount: x.commission || "", owner_name: "" })}
            onDeletePay={(p, pay) => window.confirm("Ödeme silinsin mi?") && run(() => api.delete(`/club/members/${m.id}/packages/${p.id}/payments/${pay.id}`), "Ödeme silindi")}
            isAdmin={user?.role === "admin"}
            onMealDated={(p) => setMealDlg({ memberId: m.id, pkgId: p.id, name: m.name, count: "1", date: todayISO(), note: "" })}
            onEditUsage={(p, x) => setUsageDlg({ memberId: m.id, pkgId: p.id, idx: x.idx, count: String(Math.abs(x.delta)), kind: x.delta < 0 ? "use" : "refund", date: new Date(x.at).toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" }), note: x.note || "" })}
            onDeleteUsage={(p, x) => window.confirm(`Bu öğün hareketi (${x.delta}) silinsin mi? Kalan öğün ve stok geri alınır.`) && run(() => api.delete(`/club/members/${m.id}/packages/${p.id}/usage/${x.idx}`), "Hareket silindi")}
          />
        ))}
      </div>

      <Dialog open={!!mealDlg} onOpenChange={(o) => !o && setMealDlg(null)}>
        <DialogContent data-testid="meal-dated-dialog">
          <DialogHeader><DialogTitle>Geçmiş Tarihli Öğün Kullan — {mealDlg?.name}</DialogTitle></DialogHeader>
          {mealDlg && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label>Tarih<Input className="mt-1" type="date" max={todayISO()} value={mealDlg.date} onChange={(e) => setMealDlg({ ...mealDlg, date: e.target.value })} data-testid="meal-dated-date" /></label>
              <label>Öğün sayısı<Input className="mt-1" inputMode="numeric" value={mealDlg.count} onChange={(e) => setMealDlg({ ...mealDlg, count: e.target.value })} data-testid="meal-dated-count" /></label>
              <label className="col-span-2">Not<Input className="mt-1" value={mealDlg.note} onChange={(e) => setMealDlg({ ...mealDlg, note: e.target.value })} data-testid="meal-dated-note" /></label>
              <p className="col-span-2 text-xs text-muted-foreground">Öğün seçtiğiniz güne işlenir; Öğün Kâr/Zarar o güne sayar, stok bugün düşer.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMealDlg(null)}>Vazgeç</Button>
            <Button onClick={saveMealDated} data-testid="meal-dated-save">Öğün Kullan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!usageDlg} onOpenChange={(o) => !o && setUsageDlg(null)}>
        <DialogContent data-testid="usage-dialog">
          <DialogHeader><DialogTitle>Öğün Hareketini Düzenle</DialogTitle></DialogHeader>
          {usageDlg && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label>İşlem
                <select className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={usageDlg.kind} onChange={(e) => setUsageDlg({ ...usageDlg, kind: e.target.value })} data-testid="usage-kind"><option value="use">Öğün kullanımı (satılan)</option><option value="refund">İade</option></select>
              </label>
              <label>Öğün sayısı<Input className="mt-1" inputMode="numeric" value={usageDlg.count} onChange={(e) => setUsageDlg({ ...usageDlg, count: e.target.value })} data-testid="usage-count" /></label>
              <label>Tarih<Input className="mt-1" type="date" value={usageDlg.date} onChange={(e) => setUsageDlg({ ...usageDlg, date: e.target.value })} data-testid="usage-date" /></label>
              <label>Not<Input className="mt-1" value={usageDlg.note} onChange={(e) => setUsageDlg({ ...usageDlg, note: e.target.value })} data-testid="usage-note" /></label>
              <p className="col-span-2 text-xs text-muted-foreground">Kalan öğün ve stok farka göre otomatik düzeltilir; Öğün Kâr/Zarar bu tarihe göre sayar.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUsageDlg(null)}>Vazgeç</Button>
            <Button onClick={saveUsage} data-testid="usage-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!memberDlg} onOpenChange={(o) => !o && setMemberDlg(null)}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle>{memberDlg?.id ? "Üyeyi Düzenle" : "Yeni Üye"}</DialogTitle></DialogHeader>
          {memberDlg && (
            <div className="space-y-3">
              {[["name", "Ad Soyad"], ["phone", "Telefon"], ["email", "E-posta"], ["note", "Not"]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label>
                  <Input value={memberDlg[k]} data-testid={`member-${k}`} onChange={(e) => setMemberDlg({ ...memberDlg, [k]: e.target.value })} /></div>
              ))}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={memberDlg.active} data-testid="member-active" onChange={(e) => setMemberDlg({ ...memberDlg, active: e.target.checked })} /> Aktif üye</label>
            </div>
          )}
          <DialogFooter><Button onClick={saveMember} data-testid="member-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pkgDlg} onOpenChange={(o) => !o && setPkgDlg(null)}>
        <DialogContent data-testid="package-dialog">
          <DialogHeader><DialogTitle>Öğün Paketi Tanımla</DialogTitle></DialogHeader>
          {pkgDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Öğün sayısı</Label><Input type="number" min="1" value={pkgDlg.meals} data-testid="package-meals" onChange={(e) => setPkgDlg({ ...pkgDlg, meals: e.target.value })} /></div>
              <div><Label className="text-xs">Paket bedeli (₺)</Label><Input type="number" min="0" step="0.01" value={pkgDlg.price} data-testid="package-price" onChange={(e) => setPkgDlg({ ...pkgDlg, price: e.target.value })} /></div>
              <div><Label className="text-xs">Paket tarihi (geçmiş seçilebilir)</Label><Input type="date" max={todayISO()} value={pkgDlg.date} data-testid="package-date" onChange={(e) => setPkgDlg({ ...pkgDlg, date: e.target.value })} /></div>
              <div><Label className="text-xs">Not</Label><Input value={pkgDlg.note} data-testid="package-note" onChange={(e) => setPkgDlg({ ...pkgDlg, note: e.target.value })} /></div>
              <p className="col-span-2 text-xs text-muted-foreground">Geçmişte sattığınız paketler için eski tarihi seçin; ardından "Tahsilat" ile ödemeleri de alındığı tarihe girebilirsiniz.</p>
            </div>
          )}
          <DialogFooter><Button onClick={savePkg} data-testid="package-save">Paketi Ekle</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{payDlg?.payId ? "Tahsilatı Düzenle" : "Tahsilat Kaydet"}</DialogTitle><p className="text-xs text-muted-foreground">{payDlg?.payId ? "Tutar, ödeme türü, tarih ve sahip payı güncellenir; gelir/gider kayıtları da düzeltilir." : "Geriye dönük ödeme için tarihi değiştirin — tahsilat ve sahip payı o güne işlenir."}</p></DialogHeader>
          {payDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tutar (₺)</Label><Input type="number" min="0" step="0.01" value={payDlg.amount} data-testid="payment-amount" onChange={(e) => setPayDlg({ ...payDlg, amount: e.target.value })} /></div>
              <div><Label className="text-xs">Ödeme türü</Label>
                <Select value={payDlg.payment_type} onValueChange={(v) => setPayDlg({ ...payDlg, payment_type: v })}>
                  <SelectTrigger data-testid="payment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Nakit", "Kart", "Havale"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Tahsilat tarihi (geçmiş seçilebilir)</Label><Input type="date" value={payDlg.date} max={todayISO()} data-testid="payment-date" onChange={(e) => setPayDlg({ ...payDlg, date: e.target.value })} /></div>
              <div><Label className="text-xs">Not</Label><Input value={payDlg.note} data-testid="payment-note" onChange={(e) => setPayDlg({ ...payDlg, note: e.target.value })} /></div>
              <div className="col-span-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">Kulüp sahibi payı</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Tutar (₺)</Label><Input type="number" min="0" step="0.01" placeholder="örn. 900" value={payDlg.commission_amount} data-testid="payment-commission-amount" onChange={(e) => setPayDlg({ ...payDlg, commission_amount: e.target.value })} /></div>
                  <div><Label className="text-xs">Kulüp sahibi (isteğe bağlı)</Label><Input placeholder="Ad" value={payDlg.owner_name} data-testid="payment-owner-name" onChange={(e) => setPayDlg({ ...payDlg, owner_name: e.target.value })} /></div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="payment-commission-preview">
                  {toNum(payDlg.commission_amount) > 0
                    ? `Gider olarak yazılacak: ${formatTRY(toNum(payDlg.commission_amount))} · Kulüp net: ${formatTRY(toNum(payDlg.amount) - toNum(payDlg.commission_amount))}`
                    : "Tutar girilmezse pay yazılmaz"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={savePay} data-testid="payment-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <FinanceDetailDialog detail={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, warn }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground"><span>{label}</span><Icon className={`h-4 w-4 ${warn ? "text-destructive" : "text-primary"}`} /></div>
    <p className={`mt-2 font-heading text-2xl font-extrabold ${warn ? "text-destructive" : ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);
